import {
  validateLoginIdentifier,
  validateOptionalPhone
} from './contactFieldValidation';
import {
  fetchAssignedAppsForGroupsPeriod,
  fetchTrackOptionsForGroupsPeriod,
  fetchPathwayOptionsForGroupsPeriod,
  fetchVendorOptionsForGroupsPeriod,
  formatExtraColumnMappingsForUi,
  resolveExtraColumnAppMappings,
  validateRowStudentInfoFields,
  validateStudentInfoFieldValue
} from './bulkUserCsvAppFields';
import { APP_SLUGS } from '../constants/appSlugs';
import { getStudentInformationFieldDef } from '../constants/studentInformationFields';

export const BULK_USER_CSV_REQUIRED_COLUMNS = ['first name', 'last name', 'email'];
export const BULK_USER_CSV_OPTIONAL_COLUMNS = ['phone'];
export const BULK_USER_CSV_COLUMNS = [
  ...BULK_USER_CSV_REQUIRED_COLUMNS,
  ...BULK_USER_CSV_OPTIONAL_COLUMNS
];

/** Optional identity columns — used only when the matching import checkbox is enabled. */
export const BULK_USER_CSV_IDENTITY_COLUMNS = {
  userId: {
    key: 'user id',
    label: 'User ID',
    aliases: ['user id', 'userid', 'user_id']
  },
  ugpId: {
    key: 'ugp id',
    label: 'UGP ID',
    aliases: [
      'ugp id',
      'ugpid',
      'ugp_id',
      'user groups periods id',
      'user_groups_periods_id',
      'user group period id'
    ]
  }
};

/** Profile fields that can be overwritten for existing users. */
export const BULK_OVERWRITE_USER_FIELDS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'phone', label: 'Phone' }
];

const HEADER_ALIASES = {
  'first name': ['first name', 'firstname', 'first_name'],
  'last name': ['last name', 'lastname', 'last_name'],
  email: ['email', 'e-mail'],
  phone: ['phone', 'telephone', 'mobile']
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function normalizeCsvHeader(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
}

export function isValidImportUuid(value) {
  return UUID_RE.test(String(value ?? '').trim());
}

function isStandardColumnHeader(header) {
  const normalized = normalizeCsvHeader(header);
  return BULK_USER_CSV_COLUMNS.some((column) => {
    const aliases = HEADER_ALIASES[column] || [column];
    return aliases.includes(normalized);
  });
}

function isIdentityColumnHeader(header) {
  const normalized = normalizeCsvHeader(header);
  return Object.values(BULK_USER_CSV_IDENTITY_COLUMNS).some((column) =>
    column.aliases.includes(normalized)
  );
}

/**
 * Locate optional User ID / UGP ID columns in a CSV header row.
 * Returns null indexes when a column is absent.
 */
export function detectIdentityColumnIndexes(headers) {
  if (!Array.isArray(headers)) {
    return {
      userId: { found: false, index: -1, header: null },
      ugpId: { found: false, index: -1, header: null }
    };
  }

  const normalized = headers.map(normalizeCsvHeader);
  const result = {};

  for (const [key, column] of Object.entries(BULK_USER_CSV_IDENTITY_COLUMNS)) {
    const index = normalized.findIndex((header) => column.aliases.includes(header));
    result[key] = {
      found: index >= 0,
      index,
      header: index >= 0 ? String(headers[index] ?? '').trim() : null
    };
  }

  return result;
}

/**
 * CSV headers that are not part of the sample sheet (first name, last name, email, phone)
 * and are not identity columns (User ID / UGP ID).
 */
export function getExtraCsvColumnHeaders(headers) {
  if (!Array.isArray(headers)) return [];
  return headers
    .map((raw, index) => ({ raw: String(raw ?? '').trim(), index }))
    .filter(({ raw }) => raw && !isStandardColumnHeader(raw) && !isIdentityColumnHeader(raw));
}

/**
 * RFC 4180-style CSV parser (commas, quotes, escaped quotes, apostrophes in quoted fields).
 */
export function parseCsvText(text) {
  const source = String(text ?? '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];

    if (inQuotes) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\r') {
      if (source[i + 1] === '\n') {
        i += 1;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (inQuotes) {
    throw new Error('CSV has an unclosed quote. Wrap fields that contain commas in double quotes.');
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter((cells) => cells.some((cell) => String(cell).trim() !== ''));
}

function escapeCsvField(value) {
  const text = String(value ?? '');
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function buildBulkUserCsvSample() {
  const header = BULK_USER_CSV_COLUMNS.join(',');
  const examples = [
    ['Jane', 'Doe', 'jane.doe@school.org', '555-0100'],
    ['John', "O'Brien", 'john.obrien@school.org', '555-0101'],
    ['Mary, Ann', 'Smith', 'mary.smith@school.org', '']
  ];
  const lines = [header, ...examples.map((row) => row.map(escapeCsvField).join(','))];
  return `${lines.join('\r\n')}\r\n`;
}

export function downloadBulkUserCsvSample() {
  const blob = new Blob([buildBulkUserCsvSample()], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'bulk-users-sample.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function resolveColumnIndexes(headers) {
  const normalized = headers.map(normalizeCsvHeader);
  const indexes = {};

  for (const column of BULK_USER_CSV_COLUMNS) {
    const aliases = HEADER_ALIASES[column] || [column];
    const index = normalized.findIndex((header) => aliases.includes(header));
    indexes[column] = index;
  }

  return indexes;
}

function cellValue(row, index) {
  if (index < 0) return '';
  return String(row[index] ?? '').trim();
}

function buildExistingEmailSet(existingEmails) {
  return new Set(
    (existingEmails || [])
      .map((email) => String(email || '').trim().toLowerCase())
      .filter(Boolean)
  );
}

/**
 * Build default overwrite-column selection from validated mappings + standard fields.
 */
export function buildDefaultOverwriteColumns(extraColumnMappings = []) {
  const columns = {};
  for (const field of BULK_OVERWRITE_USER_FIELDS) {
    columns[field.key] = true;
  }
  for (const mapping of extraColumnMappings || []) {
    if (mapping.fieldKey) {
      columns[mapping.fieldKey] = true;
    }
  }
  return columns;
}

export function describeOverwriteColumn(key) {
  const userField = BULK_OVERWRITE_USER_FIELDS.find((f) => f.key === key);
  if (userField) return userField.label;
  return getStudentInformationFieldDef(key)?.label || key;
}

/**
 * Validate parsed CSV rows. Returns only when every data row passes; no partial success.
 * When extra columns are present, assignedApps must reflect app_assignments on the group-period.
 *
 * When overwriteExisting is false (default) and ignoreDuplicates is false, emails already in
 * the system are errors. When overwriteExisting is true, those rows are returned as
 * overwriteCandidates for UI confirmation (and excluded from `rows` until confirmed).
 */
function emptyIdentityColumns() {
  return detectIdentityColumnIndexes(null);
}

export function validateBulkUserCsvRows(
  parsedRows,
  {
    existingEmails = [],
    assignedApps = null,
    ignoredExtraColumns = [],
    ignoreDuplicates = false,
    overwriteExisting = false,
    useUserIds = false,
    useUgpIds = false,
    fieldOptions = {}
  } = {}
) {
  const errors = [];
  const skippedDuplicates = [];
  const overwriteCandidates = [];
  const ignoredHeaders = new Set(
    (ignoredExtraColumns || []).map((header) => String(header ?? '').trim()).filter(Boolean)
  );

  if (!Array.isArray(parsedRows) || parsedRows.length === 0) {
    return {
      ok: false,
      errors: [{ row: 0, message: 'CSV file is empty.' }],
      rows: [],
      extraColumnMappings: [],
      overwriteCandidates: [],
      skippedDuplicates: [],
      identityColumns: emptyIdentityColumns()
    };
  }

  const identityColumns = detectIdentityColumnIndexes(parsedRows[0]);
  const columnIndexes = resolveColumnIndexes(parsedRows[0]);
  for (const column of BULK_USER_CSV_REQUIRED_COLUMNS) {
    if (columnIndexes[column] < 0) {
      errors.push({
        row: 1,
        message: `Missing required column "${column}". Download the sample file for the correct headers.`
      });
    }
  }
  if (errors.length > 0) {
    return {
      ok: false,
      errors,
      rows: [],
      extraColumnMappings: [],
      overwriteCandidates: [],
      skippedDuplicates: [],
      identityColumns
    };
  }

  const allExtraColumns = getExtraCsvColumnHeaders(parsedRows[0]);
  let uiExtraColumnMappings = [];
  if (allExtraColumns.length > 0 && Array.isArray(assignedApps)) {
    const previewResult = resolveExtraColumnAppMappings(allExtraColumns, assignedApps);
    uiExtraColumnMappings = formatExtraColumnMappingsForUi(previewResult.mappings);
  }

  const extraColumns = allExtraColumns.filter((column) => !ignoredHeaders.has(column.raw));
  let extraColumnMappings = [];
  let studentInfoMetadata = {};

  if (extraColumns.length > 0) {
    if (!Array.isArray(assignedApps)) {
      errors.push({
        row: 0,
        message: 'Extra columns were found but app assignments for this group-period could not be checked.'
      });
      return {
        ok: false,
        errors,
        rows: [],
        extraColumnMappings: uiExtraColumnMappings,
        overwriteCandidates: [],
        skippedDuplicates: [],
        identityColumns
      };
    }

    const mappingResult = resolveExtraColumnAppMappings(extraColumns, assignedApps);
    errors.push(...mappingResult.errors);
    extraColumnMappings = mappingResult.mappings;
    studentInfoMetadata =
      extraColumnMappings.find((mapping) => mapping.appSlug === APP_SLUGS.STUDENT_INFORMATION)
        ?.metadata || {};

    if (errors.length > 0) {
      return {
        ok: false,
        errors,
        rows: [],
        extraColumnMappings: uiExtraColumnMappings,
        overwriteCandidates: [],
        skippedDuplicates: [],
        identityColumns
      };
    }
  }

  const needsTrackOptions = extraColumnMappings.some(
    (mapping) => mapping.fieldKey === 'track_period_id'
  );
  if (needsTrackOptions && !(fieldOptions.track_period_id || []).length) {
    errors.push({
      row: 0,
      message:
        'A Track column was found but no schedule tracks are available for this organization. Add track periods (e.g. "Track A") in Schedule Builder, or uncheck the Track column.'
    });
    return {
      ok: false,
      errors,
      rows: [],
      extraColumnMappings: uiExtraColumnMappings,
      overwriteCandidates: [],
      skippedDuplicates: [],
      identityColumns
    };
  }

  const needsPathwayOptions = extraColumnMappings.some(
    (mapping) => mapping.fieldKey === 'primary_pathway'
  );
  if (needsPathwayOptions && !(fieldOptions.primary_pathway || []).length) {
    errors.push({
      row: 0,
      message:
        'A Primary pathway column was found but no taxonomy concentrations are available for this organization. Add concentrations in Taxonomy, or uncheck the Primary pathway column.'
    });
    return {
      ok: false,
      errors,
      rows: [],
      extraColumnMappings: uiExtraColumnMappings,
      overwriteCandidates: [],
      skippedDuplicates: [],
      identityColumns
    };
  }

  const needsVendorOptions = extraColumnMappings.some(
    (mapping) => mapping.fieldKey === 'vendor_groups_periods_id'
  );
  if (needsVendorOptions && !(fieldOptions.vendor_groups_periods_id || []).length) {
    errors.push({
      row: 0,
      message:
        'A Vendor column was found but no vendor partner groups are available for this organization. Ensure team groups (e.g. FA01, FA16) exist under this org, or uncheck the Vendor column.'
    });
    return {
      ok: false,
      errors,
      rows: [],
      extraColumnMappings: uiExtraColumnMappings,
      overwriteCandidates: [],
      skippedDuplicates: [],
      identityColumns
    };
  }

  if (useUserIds && !identityColumns.userId.found) {
    errors.push({
      row: 1,
      message: 'Use User ID is enabled, but no "User ID" column was found in the CSV.'
    });
  }
  if (useUgpIds && !identityColumns.ugpId.found) {
    errors.push({
      row: 1,
      message: 'Use UGP ID is enabled, but no "UGP ID" column was found in the CSV.'
    });
  }
  if (errors.length > 0) {
    return {
      ok: false,
      errors,
      rows: [],
      extraColumnMappings: uiExtraColumnMappings,
      overwriteCandidates: [],
      skippedDuplicates: [],
      identityColumns
    };
  }

  const dataRows = parsedRows.slice(1);
  if (dataRows.length === 0) {
    return {
      ok: false,
      errors: [{ row: 0, message: 'CSV has a header row but no user rows.' }],
      rows: [],
      extraColumnMappings: uiExtraColumnMappings,
      overwriteCandidates: [],
      skippedDuplicates: [],
      identityColumns
    };
  }

  const systemEmails = buildExistingEmailSet(existingEmails);
  const seenInFile = new Set();
  const seenUserIds = new Set();
  const seenUgpIds = new Set();
  const validatedRows = [];

  dataRows.forEach((row, index) => {
    const rowNumber = index + 2;
    const firstName = cellValue(row, columnIndexes['first name']);
    const lastName = cellValue(row, columnIndexes['last name']);
    const emailRaw = cellValue(row, columnIndexes.email);
    const phoneRaw = cellValue(row, columnIndexes.phone);

    if (!firstName || !lastName) {
      errors.push({
        row: rowNumber,
        message: 'First and last name are required.'
      });
      return;
    }

    const emailCheck = validateLoginIdentifier(emailRaw, { required: true });
    if (!emailCheck.ok) {
      errors.push({ row: rowNumber, message: emailCheck.message });
      return;
    }

    const phoneCheck = validateOptionalPhone(phoneRaw);
    if (!phoneCheck.ok) {
      errors.push({ row: rowNumber, message: phoneCheck.message });
      return;
    }

    const emailKey = emailCheck.value.toLowerCase();
    if (seenInFile.has(emailKey)) {
      errors.push({
        row: rowNumber,
        message: `Email "${emailCheck.value}" appears more than once in this CSV.`
      });
      return;
    }
    seenInFile.add(emailKey);

    let importUserId = null;
    if (useUserIds) {
      const rawUserId = cellValue(row, identityColumns.userId.index);
      if (!rawUserId) {
        errors.push({
          row: rowNumber,
          message: `User ID is required when "${identityColumns.userId.header}" is enabled.`
        });
        return;
      }
      if (!isValidImportUuid(rawUserId)) {
        errors.push({
          row: rowNumber,
          message: `User ID "${rawUserId}" is not a valid UUID.`
        });
        return;
      }
      const userIdKey = rawUserId.toLowerCase();
      if (seenUserIds.has(userIdKey)) {
        errors.push({
          row: rowNumber,
          message: `User ID "${rawUserId}" appears more than once in this CSV.`
        });
        return;
      }
      seenUserIds.add(userIdKey);
      importUserId = rawUserId.toLowerCase();
    }

    let importUgpId = null;
    if (useUgpIds) {
      const rawUgpId = cellValue(row, identityColumns.ugpId.index);
      if (!rawUgpId) {
        errors.push({
          row: rowNumber,
          message: `UGP ID is required when "${identityColumns.ugpId.header}" is enabled.`
        });
        return;
      }
      if (!isValidImportUuid(rawUgpId)) {
        errors.push({
          row: rowNumber,
          message: `UGP ID "${rawUgpId}" is not a valid UUID.`
        });
        return;
      }
      const ugpIdKey = rawUgpId.toLowerCase();
      if (seenUgpIds.has(ugpIdKey)) {
        errors.push({
          row: rowNumber,
          message: `UGP ID "${rawUgpId}" appears more than once in this CSV.`
        });
        return;
      }
      seenUgpIds.add(ugpIdKey);
      importUgpId = rawUgpId.toLowerCase();
    }

    const existsInSystem = systemEmails.has(emailKey);
    if (existsInSystem && ignoreDuplicates && !overwriteExisting) {
      skippedDuplicates.push({
        row: rowNumber,
        email: emailCheck.value,
        reason: 'email_exists'
      });
      return;
    }
    if (existsInSystem && !ignoreDuplicates && !overwriteExisting) {
      errors.push({
        row: rowNumber,
        message: `User with email "${emailCheck.value}" already exists. Enable overwrite confirmation or skip emails already in the system.`
      });
      return;
    }

    const studentInfo = {};
    const displayByField = {};
    const referenceMaps = {};
    for (const mapping of extraColumnMappings) {
      if (mapping.appSlug !== APP_SLUGS.STUDENT_INFORMATION) continue;
      const rawValue = cellValue(row, mapping.columnIndex);
      const fieldCheck = validateStudentInfoFieldValue(
        mapping.fieldKey,
        rawValue,
        mapping.metadata,
        fieldOptions
      );
      if (!fieldCheck.ok) {
        errors.push({
          row: rowNumber,
          message: `${mapping.csvHeader}: ${fieldCheck.message}`
        });
        return;
      }
      if (fieldCheck.value !== null && fieldCheck.value !== undefined) {
        studentInfo[mapping.fieldKey] = fieldCheck.value;
        if (mapping.fieldKey === 'track_period_id' && fieldCheck.matchedLabel) {
          displayByField.track_period_label = fieldCheck.matchedLabel;
          referenceMaps.track_period_id = {
            id: String(fieldCheck.value),
            label: fieldCheck.matchedLabel,
            sheetValue: fieldCheck.sheetValue || rawValue,
            matchMethod: fieldCheck.matchMethod || null,
            fieldKey: 'track_period_id'
          };
        }
        if (mapping.fieldKey === 'primary_pathway' && fieldCheck.matchedLabel) {
          displayByField.primary_pathway_label = fieldCheck.matchedLabel;
          referenceMaps.primary_pathway = {
            id: String(fieldCheck.value),
            label: fieldCheck.matchedLabel,
            sheetValue: fieldCheck.sheetValue || rawValue,
            matchMethod: fieldCheck.matchMethod || null,
            fieldKey: 'primary_pathway'
          };
        }
        if (mapping.fieldKey === 'vendor_groups_periods_id' && fieldCheck.matchedLabel) {
          displayByField.vendor_groups_periods_label = fieldCheck.matchedLabel;
          referenceMaps.vendor_groups_periods_id = {
            id: String(fieldCheck.value),
            label: fieldCheck.matchedLabel,
            sheetValue: fieldCheck.sheetValue || rawValue,
            matchMethod: fieldCheck.matchMethod || null,
            fieldKey: 'vendor_groups_periods_id'
          };
        }
      }
    }

    const studentRowError = validateRowStudentInfoFields(studentInfo, studentInfoMetadata);
    if (studentRowError) {
      errors.push({ row: rowNumber, message: studentRowError });
      return;
    }

    const validatedRow = {
      row_number: rowNumber,
      first_name: firstName,
      last_name: lastName,
      email: emailCheck.value,
      phone: phoneCheck.value,
      exists_in_system: existsInSystem
    };

    if (importUserId) {
      validatedRow.id = importUserId;
    }
    if (importUgpId) {
      validatedRow.user_groups_periods_id = importUgpId;
    }

    if (Object.keys(studentInfo).length > 0) {
      validatedRow.appData = { [APP_SLUGS.STUDENT_INFORMATION]: studentInfo };
      if (displayByField.track_period_label) {
        validatedRow.track_period_label = displayByField.track_period_label;
      }
      if (displayByField.primary_pathway_label) {
        validatedRow.primary_pathway_label = displayByField.primary_pathway_label;
      }
      if (displayByField.vendor_groups_periods_label) {
        validatedRow.vendor_groups_periods_label = displayByField.vendor_groups_periods_label;
      }
      if (Object.keys(referenceMaps).length > 0) {
        validatedRow.reference_maps = referenceMaps;
      }
    }

    if (existsInSystem && overwriteExisting) {
      overwriteCandidates.push(validatedRow);
    } else {
      validatedRows.push(validatedRow);
    }
  });

  if (errors.length > 0) {
    return {
      ok: false,
      errors,
      rows: [],
      extraColumnMappings: uiExtraColumnMappings,
      overwriteCandidates: [],
      skippedDuplicates,
      identityColumns
    };
  }

  if (
    validatedRows.length === 0 &&
    overwriteCandidates.length === 0 &&
    skippedDuplicates.length > 0
  ) {
    return {
      ok: false,
      errors: [{ row: 0, message: 'All rows are duplicate emails. Nothing to import.' }],
      rows: [],
      extraColumnMappings: uiExtraColumnMappings,
      overwriteCandidates: [],
      skippedDuplicates,
      identityColumns
    };
  }

  const needsOverwriteConfirm = overwriteCandidates.length > 0;
  return {
    ok: true,
    errors: [],
    rows: validatedRows,
    extraColumnMappings: uiExtraColumnMappings,
    overwriteCandidates,
    skippedDuplicates,
    needsOverwriteConfirm,
    identityColumns
  };
}

export async function readAndValidateBulkUserCsvFile(file, options = {}) {
  const emptyResult = (errors, identityColumns = emptyIdentityColumns()) => ({
    ok: false,
    errors,
    rows: [],
    extraColumnMappings: [],
    overwriteCandidates: [],
    skippedDuplicates: [],
    identityColumns,
    assignedApps: options.assignedApps ?? null,
    fieldOptions: options.fieldOptions ?? {}
  });

  if (!file) {
    return emptyResult([{ row: 0, message: 'Choose a CSV file to import.' }]);
  }

  if (!/\.csv$/i.test(file.name) && file.type && !/csv|text\/plain/i.test(file.type)) {
    return emptyResult([{ row: 0, message: 'File must be a .csv spreadsheet.' }]);
  }

  let text;
  try {
    text = await file.text();
  } catch {
    return emptyResult([{ row: 0, message: 'Could not read the CSV file.' }]);
  }

  let parsedRows;
  try {
    parsedRows = parseCsvText(text);
  } catch (err) {
    return emptyResult([{ row: 0, message: err.message || 'Could not parse CSV.' }]);
  }

  const identityColumns =
    parsedRows.length > 0 ? detectIdentityColumnIndexes(parsedRows[0]) : emptyIdentityColumns();
  const extraColumns = parsedRows.length > 0 ? getExtraCsvColumnHeaders(parsedRows[0]) : [];
  let assignedApps = options.assignedApps ?? null;
  let fieldOptions =
    options.fieldOptions && typeof options.fieldOptions === 'object'
      ? { ...options.fieldOptions }
      : {};

  if (extraColumns.length > 0) {
    if (assignedApps == null) {
      const { groupsPeriodsId, token } = options;
      if (!groupsPeriodsId) {
        return emptyResult(
          [
            {
              row: 0,
              message:
                'This CSV includes extra columns. Select a group-period before importing so those columns can be matched to assigned apps.'
            }
          ],
          identityColumns
        );
      }
      try {
        assignedApps = await fetchAssignedAppsForGroupsPeriod(groupsPeriodsId, token);
      } catch (err) {
        return emptyResult(
          [{ row: 0, message: err.message || 'Could not load app assignments.' }],
          identityColumns
        );
      }
    }

    const previewMappings = resolveExtraColumnAppMappings(extraColumns, assignedApps).mappings;
    const { groupsPeriodsId, token } = options;

    // Prefetch option lists for every mapped reference field (ignore checkbox state).
    // Callers reuse these on later validates so toggling columns stays local/JS-only.
    const mappedTrack = previewMappings.some((m) => m.fieldKey === 'track_period_id');
    const mappedPathway = previewMappings.some((m) => m.fieldKey === 'primary_pathway');
    const mappedVendor = previewMappings.some((m) => m.fieldKey === 'vendor_groups_periods_id');

    if ((mappedTrack || mappedPathway || mappedVendor) && !groupsPeriodsId) {
      return emptyResult(
        [
          {
            row: 0,
            message:
              'Select a group-period before importing Track, Primary pathway, or Vendor columns.'
          }
        ],
        identityColumns
      );
    }

    if (mappedTrack && !fieldOptions.track_period_id) {
      try {
        const tracks = await fetchTrackOptionsForGroupsPeriod(groupsPeriodsId, token);
        fieldOptions = { ...fieldOptions, track_period_id: tracks };
      } catch (err) {
        return emptyResult(
          [{ row: 0, message: err.message || 'Could not load schedule track options.' }],
          identityColumns
        );
      }
    }

    if (mappedPathway && !fieldOptions.primary_pathway) {
      try {
        const pathways = await fetchPathwayOptionsForGroupsPeriod(groupsPeriodsId, token);
        fieldOptions = { ...fieldOptions, primary_pathway: pathways };
      } catch (err) {
        return emptyResult(
          [{ row: 0, message: err.message || 'Could not load primary pathway options.' }],
          identityColumns
        );
      }
    }

    if (mappedVendor && !fieldOptions.vendor_groups_periods_id) {
      try {
        const vendorMeta =
          previewMappings.find((m) => m.fieldKey === 'vendor_groups_periods_id')?.metadata || {};
        const vendors = await fetchVendorOptionsForGroupsPeriod(
          groupsPeriodsId,
          token,
          vendorMeta
        );
        fieldOptions = { ...fieldOptions, vendor_groups_periods_id: vendors };
      } catch (err) {
        return emptyResult(
          [{ row: 0, message: err.message || 'Could not load vendor partner options.' }],
          identityColumns
        );
      }
    }
  }

  const validated = validateBulkUserCsvRows(parsedRows, {
    existingEmails: options.existingEmails,
    assignedApps,
    ignoredExtraColumns: options.ignoredExtraColumns,
    ignoreDuplicates: options.ignoreDuplicates === true,
    overwriteExisting: options.overwriteExisting === true,
    useUserIds: options.useUserIds === true,
    useUgpIds: options.useUgpIds === true,
    fieldOptions
  });

  return {
    ...validated,
    assignedApps,
    fieldOptions
  };
}
